import {
  FormattedTextSegment,
  parseFormattedText,
  renderListMarkers,
} from '@gripp/shared-logic';
import { ReactNode } from 'react';
import { StyleSheet, TextStyle } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';

/**
 * Renders the inline formatting the composer's text-style toolbar produces
 * (see `messageFormatting.ts` for the marker syntax). Returns an array of
 * nodes rather than a component so callers can splice it into the existing
 * message `<Text>` alongside mention spans, which have to stay in the same
 * text flow to wrap correctly.
 *
 * The returned elements deliberately carry NO `key`. They are rendered inside
 * react-native-hyperlink, whose `parse` destructures every child's props; on
 * React 19 that read trips the "`key` is not a prop" warning getter, which is
 * installed only on elements created with a key. Callers must therefore spread
 * these nodes as variadic children (see `userMessage.tsx`) rather than passing
 * the array, so React neither demands keys nor attaches the getter.
 */
export const renderFormattedMessageText = (
  text: string,
  options: {
    /** Called for `[label](url)` links; bare URLs stay with Hyperlink. */
    onLinkPress: (url: string) => void;
  }
): ReactNode[] =>
  parseFormattedText(renderListMarkers(text)).map((segment) => {
    // A segment with no marks is plain text. Returning the raw string (rather
    // than wrapping it in a <Text>) keeps the output identical to the
    // pre-formatting render path for the overwhelming majority of messages.
    if (!segment.url && !hasMarks(segment)) {
      return segment.text;
    }

    return (
      <Text
        style={segmentStyle(segment)}
        onPress={
          segment.url ? () => options.onLinkPress(segment.url as string) : undefined
        }
      >
        {segment.text}
      </Text>
    );
  });

const hasMarks = (segment: FormattedTextSegment): boolean =>
  Boolean(
    segment.bold ||
      segment.italic ||
      segment.underline ||
      segment.strikethrough
  );

const segmentStyle = (segment: FormattedTextSegment): TextStyle => {
  // Underline and strike-through share one RN property, so a segment carrying
  // both needs the combined value rather than a last-one-wins override.
  const decorations = [
    segment.underline ? 'underline' : undefined,
    segment.strikethrough ? 'line-through' : undefined,
  ].filter(Boolean);

  return {
    ...(segment.bold ? styles.bold : undefined),
    ...(segment.italic ? styles.italic : undefined),
    ...(segment.url ? styles.link : undefined),
    ...(decorations.length > 0
      ? { textDecorationLine: decorations.join(' ') as TextStyle['textDecorationLine'] }
      : undefined),
  };
};

const styles = StyleSheet.create({
  bold: {
    fontWeight: '700',
    color: Colors.role.textStrong,
  },
  italic: {
    fontStyle: 'italic',
  },
  link: {
    color: Colors.blue,
  },
});
